import type { Issue } from '@botpress/client'
import { Button, Card, Checkbox, Separator, showConfirmationPrompt } from '@bpinternal/ui-kit-next'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { orderBy } from 'lodash'
import { DateTime } from 'luxon'
import { Fragment, useState } from 'react'
import { match } from 'ts-pattern'
import EmptyStateIcon from '~/assets/programming-23.svg?react'
import { IdentifierDropdownMenu, Page } from '~/components'
import { Badge, EmptyState, Link, Tag } from '~/elements'
import { IssueCategory } from '~/features/issues/'
import { useResolveIssues } from '~/hooks/issues/useResolveIssues'
import { listAllIssuesQueryOptions } from '~/queries'
import { createIdentifierStudioUrl } from '~/utils'

export const Route = createFileRoute('/workspaces/$workspaceId/bots/$botId/issues/')({
  component: () => {
    return (
      <Page title="Issues">
        <IssuesList />
      </Page>
    )
  },
})

export const IssuesList = () => {
  const { botId, workspaceId } = Route.useParams()
  const [selectedIssues, setSelectedIssues] = useState<string[]>([])

  const { mutate: resolveIssues } = useResolveIssues({ botId, workspaceId })
  const issues = useSuspenseQuery(listAllIssuesQueryOptions({ botId, workspaceId })).data

  return (
    <Card className="p-0 text-sm">
      <div className="relative grid grid-cols-[auto_1fr_repeat(2,auto)] gap-x-7">
        <div className="col-span-full grid grid-cols-subgrid items-center rounded-t-xl bg-neutral-50 p-2 pr-6 font-medium">
          <Checkbox
            checked={selectedIssues.length === issues.length && issues.length > 0}
            onCheckedChange={(checked) => setSelectedIssues(checked ? issues.map((i) => i.id) : [])}
            className="-mr-8 border-neutral-300 shadow-none"
          />
          <Button
            className="w-fit disabled:bg-neutral-300 disabled:text-neutral-500"
            disabled={selectedIssues.length === 0}
            onClick={() =>
              showConfirmationPrompt(
                `Resolving issues will permanently remove them from the list. Are you sure you want to resolve ${selectedIssues.length} issues?`,
                {
                  title: 'Resolve Issues',
                }
              ).then(() => resolveIssues({ issueIds: selectedIssues }))
            }
          >
            Resolve Issues
          </Button>
          <p>Category</p>
          <p>Events</p>
        </div>
        {issues.length === 0 ? (
          <>
            <Separator className="col-span-full" />
            <EmptyState
              iconSize={6}
              icon={EmptyStateIcon}
              className="col-span-full py-16"
              title="No issues found!"
              description="Great news! No issues were found in your bot. Issues will appear here when they are detected."
            />
          </>
        ) : (
          orderBy(issues, 'lastSeenAt', 'desc').map((issue) => (
            <Fragment key={issue.id}>
              <Separator className="col-span-full" />
              <IssueRow
                issue={issue}
                selected={selectedIssues.includes(issue.id)}
                selectIssue={(selected) =>
                  setSelectedIssues((prev) => (selected ? [...prev, issue.id] : prev.filter((id) => id !== issue.id)))
                }
              />
            </Fragment>
          ))
        )}
      </div>
    </Card>
  )
}

const IssueRow = ({
  issue,
  selected,
  selectIssue,
}: {
  issue: Issue
  selected: boolean
  selectIssue: (selected: boolean) => void
}) => {
  const { workspaceId, botId } = Route.useParams()
  const { workflowId, nodeId } = issue.groupedData
  const category = IssueCategory[issue.category]

  return (
    <div className="col-span-full grid grid-cols-subgrid items-center px-2 py-4">
      <Checkbox
        className="-mr-8 self-start border-neutral-500 shadow-none"
        checked={selected}
        onCheckedChange={(checked) => selectIssue(!!checked)}
      />
      <div className="flex flex-col gap-3">
        <div className="flex flex-col">
          <div className="flex items-baseline gap-2">
            <Link
              to="/workspaces/$workspaceId/bots/$botId/issues/$issueId"
              className="font-medium"
              params={{ workspaceId, botId, issueId: issue.id }}
            >
              {issue.title}
            </Link>
            <div className="flex gap-2 text-xs text-muted-foreground">
              <p>{DateTime.fromISO(issue.lastSeenAt).toRelative()}</p>
              <p>|</p>
              <p>{DateTime.fromISO(issue.createdAt).toRelative()?.replace('ago', 'old')}</p>
            </div>
          </div>
          <p className="line-clamp-3 text-muted-foreground">{issue.description}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(issue.groupedData).map(([key, { raw, pretty }]) => {
            const studioUrl = match({ key })
              .with({ key: 'workflowId' }, () => {
                return createIdentifierStudioUrl({ botId, workflowId: raw })
              })
              .with({ key: 'nodeId' }, () => {
                return createIdentifierStudioUrl({ botId, workflowId: workflowId?.raw, nodeId: raw })
              })
              .with({ key: 'cardId' }, () => {
                return createIdentifierStudioUrl({
                  botId,
                  workflowId: workflowId?.raw,
                  nodeId: nodeId?.raw,
                  cardId: raw,
                })
              })
              .otherwise(() => undefined)

            return (
              <IdentifierDropdownMenu key={key} studioUrl={studioUrl} value={raw}>
                <Tag clickable label={key} value={pretty ?? raw} />
              </IdentifierDropdownMenu>
            )
          })}
        </div>
      </div>
      <Badge color={category.color} variant="secondary" className="w-fit justify-self-center">
        {category.title}
      </Badge>
      <p className="justify-self-center">{issue.eventsCount}</p>
    </div>
  )
}
